import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLAnchorElement} [loadMoreNext] - The load more next link.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {boolean} */
  #isLoadingNextPage = false;

  /** @type {HTMLElement[]} */
  #nextPageItemQueue = [];

  /** @type {number} */
  #lastQueuedNextPage = 0;

  /** @type {boolean} */
  #isFillingRows = false;

  /** @type {ResizeObserver | undefined} */
  #gridResizeObserver;

  /** @type {number} */
  #lastColumnCount = 0;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next').then(() => this.#fillCurrentRowFromNextPages());
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#observeGridColumns();
    this.#updateLoadMoreButton();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.#gridResizeObserver) {
      this.#gridResizeObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = type === 'next' ? this.#getNextPageToFetch() : this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    try {
      if (!page || !this.#shouldUsePage(page)) {
        return;
      }

      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.error(error);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const nextPageItemElements = await this.#appendNextPageBatch();
    if (!nextPageItemElements.length) return false;

    const rowFillItemElements = await this.#fillCurrentRowFromNextPages({ processNewElements: false });
    const appendedElements = [...nextPageItemElements, ...rowFillItemElements];

    this.#aspectRatioHelper?.processNewElements();

    const lastPage = this.#getLastPageFromElements(appendedElements);
    if (lastPage) {
      history.pushState('', '', this.#getPageUrl(lastPage).toString());
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    this.#updateLoadMoreButton();

    return true;
  }

  /**
   * Load the next page from an explicit shopper action.
   *
   * @param {Event} event
   */
  loadMoreNext = async (event) => {
    event.preventDefault();

    if (this.#isLoadingNextPage) return;

    this.#isLoadingNextPage = true;
    this.#setLoadMoreLoading(true);

    try {
      await this.#renderNextPage();
    } catch (error) {
      console.error(error);
    } finally {
      this.#isLoadingNextPage = false;
      this.#setLoadMoreLoading(false);
      this.#updateLoadMoreButton();
    }
  };

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';
    const cards = this.#getGridCards();

    if (cards.length > 0) {
      const targetCard = cards[isPrevious ? 0 : cards.length - 1];
      const currentCardPage = Number(targetCard.dataset.page);
      const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

      return {
        page,
        url: this.#getPageUrl(page),
      };
    }

    // No cards are currently rendered in the grid. This can legitimately happen when
    // every product on a raw pagination page was filtered out server-side (e.g. the
    // hide-from-collections color-group duplicates filtered in main-collection.liquid),
    // not just when we've truly run out of pages. Fall back to the raw page the grid
    // was actually rendered/fetched for so pagination keeps advancing instead of
    // reporting "no next page" the moment one page happens to render zero cards.
    const { grid } = this.refs;
    const basePage = Number(grid?.dataset.currentPage);

    if (!Number.isFinite(basePage)) return;

    const page = isPrevious ? basePage - 1 : basePage + 1;

    return {
      page,
      url: this.#getPageUrl(page),
    };
  }

  /**
   * @param {number} page
   * @returns {URL}
   */
  #getPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return url;
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getQueuedNextPage() {
    const [nextItem] = this.#nextPageItemQueue;
    const page = Number(nextItem?.dataset.page);

    if (!Number.isFinite(page)) return;

    return {
      page,
      url: this.#getPageUrl(page),
    };
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPageToFetch() {
    const nextPage = this.#getPage('next');
    if (!nextPage) return;

    const page = Math.max(nextPage.page, this.#lastQueuedNextPage + 1);
    const pageInfo = {
      page,
      url: this.#getPageUrl(page),
    };

    if (!this.#shouldUsePage(pageInfo)) return;

    return pageInfo;
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPageCandidate() {
    return this.#getQueuedNextPage() ?? this.#getNextPageToFetch();
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getGridCards() {
    const { grid } = this.refs;
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')).filter(
      (element) => element instanceof HTMLElement
    );
  }

  /**
   * @returns {boolean}
   */
  #shouldFillRows() {
    const { grid, loadMoreNext, viewMoreNext } = this.refs;

    return grid?.dataset.fillRows === 'true' && Boolean(loadMoreNext || viewMoreNext);
  }

  /**
   * @returns {number}
   */
  #getColumnCount() {
    const { grid } = this.refs;
    if (!grid) return 1;

    const gridTemplateColumns = getComputedStyle(grid).gridTemplateColumns;

    if (!gridTemplateColumns || gridTemplateColumns === 'none') return 1;

    return gridTemplateColumns.split(' ').filter(Boolean).length || 1;
  }

  #observeGridColumns() {
    if (this.#gridResizeObserver) {
      this.#gridResizeObserver.disconnect();
    }

    const { grid } = this.refs;
    if (!grid || typeof ResizeObserver === 'undefined') return;

    this.#lastColumnCount = this.#getColumnCount();
    this.#gridResizeObserver = new ResizeObserver(() => {
      const columnCount = this.#getColumnCount();

      if (!columnCount || columnCount === this.#lastColumnCount) return;

      this.#lastColumnCount = columnCount;
      this.#fillCurrentRowFromNextPages();
    });

    this.#gridResizeObserver.observe(grid);
  }

  /**
   * @returns {Promise<boolean>}
   */
  async #ensureNextPageItemQueue() {
    while (this.#nextPageItemQueue.length === 0) {
      const nextPage = this.#getNextPageToFetch();

      if (!nextPage) return false;

      if (!this.pages.has(nextPage.page)) {
        await this.#fetchSpecificPage(nextPage.page, nextPage.url);
      }

      const nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return false;

      const items = Array.from(nextPageItemElements).filter(
        (element) => element instanceof HTMLElement
      );
      this.#lastQueuedNextPage = Math.max(this.#lastQueuedNextPage, nextPage.page);

      if (items.length > 0) {
        this.#nextPageItemQueue.push(...items);
      }
    }

    return true;
  }

  /**
   * @returns {Promise<HTMLElement[]>}
   */
  async #appendNextPageBatch() {
    const { grid } = this.refs;
    if (!grid) return [];

    if (!(await this.#ensureNextPageItemQueue())) return [];

    const page = Number(this.#nextPageItemQueue[0]?.dataset.page);
    if (!Number.isFinite(page)) return [];

    /** @type {HTMLElement[]} */
    const pageItemElements = [];

    while (this.#nextPageItemQueue.length > 0) {
      const [nextItem] = this.#nextPageItemQueue;
      if (Number(nextItem?.dataset.page) !== page) break;

      const pageItemElement = this.#nextPageItemQueue.shift();
      if (pageItemElement) pageItemElements.push(pageItemElement);
    }

    grid.append(...pageItemElements);

    return pageItemElements;
  }

  /**
   * @param {{ processNewElements?: boolean }} [options]
   * @returns {Promise<HTMLElement[]>}
   */
  async #fillCurrentRowFromNextPages({ processNewElements = true } = {}) {
    const { grid } = this.refs;

    if (!grid || !this.#shouldFillRows() || this.#isFillingRows) return [];

    this.#isFillingRows = true;

    /** @type {HTMLElement[]} */
    const appendedElements = [];

    try {
      while (true) {
        const columnCount = this.#getColumnCount();

        if (columnCount <= 1) break;

        const remainder = this.#getGridCards().length % columnCount;

        if (remainder === 0) break;
        if (!(await this.#ensureNextPageItemQueue())) break;

        const neededCount = columnCount - remainder;
        const rowFillItemElements = this.#nextPageItemQueue.splice(0, neededCount);

        if (rowFillItemElements.length === 0) break;

        grid.append(...rowFillItemElements);
        appendedElements.push(...rowFillItemElements);
      }
    } finally {
      this.#isFillingRows = false;
      this.#updateLoadMoreButton();
    }

    if (appendedElements.length > 0 && processNewElements) {
      this.#aspectRatioHelper?.processNewElements();
    }

    return appendedElements;
  }

  /**
   * @param {HTMLElement[]} elements
   * @returns {number | undefined}
   */
  #getLastPageFromElements(elements) {
    const lastElement = elements[elements.length - 1];
    const page = Number(lastElement?.dataset.page);

    if (!Number.isFinite(page)) return;

    return page;
  }

  syncFilledRows() {
    this.#fillCurrentRowFromNextPages();
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  #updateLoadMoreButton() {
    const { loadMoreNext } = this.refs;

    if (!loadMoreNext) return;

    const nextPage = this.#getNextPageCandidate();
    const hasNextPage = Boolean(nextPage && this.#shouldUsePage(nextPage));

    loadMoreNext.hidden = !hasNextPage;
    loadMoreNext.setAttribute('aria-disabled', String(!hasNextPage));

    if (nextPage) {
      loadMoreNext.href = nextPage.url.toString();
    }
  }

  /**
   * @param {boolean} loading
   */
  #setLoadMoreLoading(loading) {
    const { loadMoreNext } = this.refs;

    if (!loadMoreNext) return;

    loadMoreNext.setAttribute('aria-busy', String(loading));
    loadMoreNext.setAttribute('aria-disabled', String(loading));
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#nextPageItemQueue = [];
    this.#lastQueuedNextPage = 0;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver((mutations) => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;
      const gridChanged = mutations.some((mutation) => mutation.type === 'childList');

      if (newLastPage !== currentLastPage || gridChanged) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();
        this.#observeGridColumns();
        this.#updateLoadMoreButton();

        // Fetch the next page
        this.#fetchPage('next').then(() => this.#fillCurrentRowFromNextPages());
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
